import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions } from 'ag-grid-community';
import { AgGridHelperService } from 'src/app/reusables/services/ag-grid-helper.service';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { CvConfigurationService } from '../../../../reusables/services/cv-configuration.service';
import { ContactStatesSkillCellRendererComponent } from './contact-states-skill-cell-renderer/contact-states-skill-cell-renderer.component';
import { ContactStatesSkillService } from './contact-states-skill.service';
import { ContactStatesSkillObject, EStateName } from './contat-states-skill.model';

@Component({
    selector: 'cv-contact-states-skill',
    templateUrl: './contact-states-skill.component.html',
    styleUrls: ['./contact-states-skill.component.scss']
})
export class ContactStatesSkillComponent implements OnInit {
    @ViewChild('contactStatesSkillWidgetGrid') agGrid: AgGridAngular;
    @Input() moduleKey: string;
    @Input() moduleManager: ModuleManager;

    autoApiCounter = 0;
    isDataLoading = false;
    gridOptions: GridOptions = null;
    data: ContactStatesSkillObject[];
    tableState: any;
    noData = false;
    contactStates: { baseName: string; localizedName: string }[] = [
        { baseName: EStateName.InQueue, localizedName: 'InQueue' },
        { baseName: EStateName.WithAgent, localizedName: 'With Agent' },
        { baseName: EStateName.PostQueue, localizedName: 'PostQueue' },
        { baseName: EStateName.PreQueue, localizedName: 'PreQueue' },
        { baseName: EStateName.OutQueue, localizedName: 'OutQueue' },
    ];
    showFilter = false;
    filterValue = '';
    stateSaveTimer = null;
    restoreInProgress = false;
    dataLoadSuccess = false;

    //For cxone search placeholder localization
    searchPlaceholder = "";
    localeText = {};

    private get isResizeWaiting() { return this.moduleManager?.container.isResizeWaiting };
    private stateRestoreInProgress = false; // Flag to prevent state save while restoring state until the widget is "stable"
    private isUserSized = false;
    private previousFilterValue = null;
    private STATE_COLORS: { [color: string]: string } = {
        green: "#66CB36",
        purple: "#7C52BF",
        yellow: "#F8BF13",
        red: "#BD2026",
        blue: "#338CCA",
        orange: "#F9920C",
        cyan: "#00BBBD",
        brown: "#A47748",
        pink: "#E74181",
        black: "#000",
        teal: "#6095A2",
    };

    constructor(
        private agGridHelperService: AgGridHelperService,
        private service: ContactStatesSkillService,
        private cvLocalizationService: CVLocalizationService,
        public config: CvConfigurationService,
    ) { }

    async ngOnInit() {

        await Promise.all([
            this.cvLocalizationService.GetSection("Contact_States_By_Skill"),
            this.cvLocalizationService.GetSection("Error_Module"),
            this.cvLocalizationService.GetSection("Common")
        ]);

        this.moduleManager.setOptions({
            widgetMode: false,
            onSettingsApplied: settings => this.loadData(false),
            hasRefresh: true,
            refreshType: ModuleManagerRefreshType.VeryShort,
            onRefresh: isAutoRefresh => this.loadData(isAutoRefresh),
            canRefresh: () => !this.isDataLoading
        });

        this.searchPlaceholder = this.cvLocalizationService.getCachedText("Common", "Search", "Search");
        this.localeText = await this.agGridHelperService.getLocalizations();
        this.getLegendLocalizations();

        this.initializeGrid();


        this.moduleManager.addState({
            name: "tableState",
            getStateData: () => this.tableState,
            onStateLoaded: stateData => {
                if (!stateData) { // No table state loaded
                    return;
                }

                this.tableState = stateData;
                this.restoreColumnState();
            },
            autoLoad: true
        });

        this.moduleManager.addState({
            name: "isUserSized",
            getStateData: () => this.isUserSized,
            onStateLoaded: stateData => {
                if (!stateData) {
                    return;
                }

                this.isUserSized = stateData;
            },
            autoLoad: true
        });

        this.moduleManager.addHeaderItem({
            name: "Search",
            icon: "search",
            click: () => this.toggleOpenSearch(),
        });

        this.loadData();
    }

    async loadData(isAutoRefresh = false) {
        if (this.isDataLoading)
            return;
        this.isDataLoading = true;

        this.moduleManager.showLoader = !isAutoRefresh;

        try {
            const response = await this.service.LoadData(this.moduleKey, isAutoRefresh);
            const noDataLoad = this.noData;
            this.data = response.data;

            this.data.forEach((row) => {
                row.Total = 0;
                row.ContactCounts.forEach(contact => {
                    row.Total += contact;
                });
            });

            this.noData = this.data.length === 0;

            if (this.agGrid?.api) {
                this.agGrid?.api.setGridOption('rowData', this.data);
                this.agGrid?.api.redrawRows();
            }

            this.moduleManager.setAutoRefreshReady();
            this.moduleManager.showLoader = false;

        } catch (e) {
            this.moduleManager.handleLoadError(e, isAutoRefresh);
        } finally {
            this.applyFilter();
            this.dataLoadSuccess = !this.noData;
            this.isDataLoading = false;
        };
    }

    initializeGrid() {
        this.gridOptions = this.agGridHelperService.createGridOptions({
            components: {
                contactStatesSkillCellRenderer: ContactStatesSkillCellRendererComponent,
            },
            context: {
                largestStateTotal: 0,
                getStateColor: state => this.getStateColor(state),
                getLocalizedStateName: state => this.getLocalizedStateName(state),
            },
            isExternalFilterPresent: () => this.filterValue.length > 0,
            doesExternalFilterPass: node => this.agGridHelperService.doesExternalFilterPass(node, this.filterValue),
            defaultColDef: {
                minWidth: this.agGridHelperService.defaultMinWidth,
            },
            columnDefs: [
                {
                    colId: "SkillName",
                    field: "SkillName",
                    headerName: this.cvLocalizationService.getCachedText("Contact_States_By_Skill", "Skill_Name", "Skill Name"),
                    valueFormatter: (params) => {
                        if(params.value === null || params.value === "")
                            return this.cvLocalizationService.getCachedText("Contact_States_By_Skill", "No_Skill", "(No skill)");
                    },
                    resizable: true,
                    sortable: true,
                    suppressSizeToFit: true,
                    filter: 'agTextColumnFilter',
                },
                {
                    colId: "Total",
                    field: "Total",
                    headerName: this.cvLocalizationService.getCachedText("Contact_States_By_Skill", "Total", "Total"),
                    valueGetter: (params) => {
                        return params.data.Total.toString();
                    },
                    cellRenderer: "contactStatesSkillCellRenderer",
                    resizable: true,
                    sortable: true,
                    filter: 'agTextColumnFilter',
                }
            ],
            getRowId: ({ data: rowData }) => rowData.SkillID,
            rowData: [],
            onGridReady: event => {
                if (this.data) {
                    this.agGrid?.api.setGridOption('rowData', this.data);
                }

                this.stateRestoreInProgress = true; // Prevent saving while the widget is still loading the initial states

                this.restoreColumnState(); //Needed to restore columns size saved on the tableState.
                if (!this.tableState) {
                    this.agGridHelperService.sizeColumnsSmart(event); //This will trigger the publish bar only once if the tableState is null.
                    this.updateTableState();
                }

                setTimeout(() => this.stateRestoreInProgress = false, 0); // Wait for the next tick to let events finish before allowing state save
            },
            onSortChanged: () => this.updateTableState(),
            onColumnMoved: () => this.updateTableState(),
            onColumnPinned: () => this.updateTableState(),
            onColumnVisible: () => this.updateTableState(),
            onColumnResized: event => {
                if (event.source == "uiColumnResized" || (event.source == "autosizeColumns" && !this.moduleManager.container.isTogglingFullscreen)) {
                    this.updateTableState();
                }
            },
            overlayNoRowsTemplate: this.config.isCxOne ?
                `<div class="error-container" style="padding-top: 48px;">
                    <svg class="mb-2">
                        <use href="../App_Themes/CXone/icons/no-results.svg#no-results"></use>
                    </svg>
                    <p class="cxone-error-text">
                        ${this.cvLocalizationService.getCachedText("Error_Module", "CXOne_No_Results", "No results found")}
                    </p>
                </div>` : null,
            onFilterChanged: () => this.updateTableState(),
            onGridSizeChanged: event => {
                if (this.isResizeWaiting) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                    this.updateTableState();
                    this.isUserSized = true;
                } else if (this.moduleManager.container.isFullscreen) {
                    this.agGridHelperService.sizeColumnsSmart(event);
                } else {
                    this.restoreColumnState();
                }
            },
            localeText: this.localeText,
            suppressDragLeaveHidesColumns: true,
            suppressScrollOnNewData: true,
            suppressNoRowsOverlay: true,
            rowHeight: 36,
            headerHeight: 44
        });
    }

    queueStateSave() {
        if (this.stateRestoreInProgress)
            return;

        // Cancel previous save attempt, since it hasn't been 500ms, yet.
        if (this.stateSaveTimer) {
            clearTimeout(this.stateSaveTimer);
            this.stateSaveTimer = null;
        }

        // Create a timer to save the state 500ms from now.
        this.stateSaveTimer = window.setTimeout(async () => {
            this.stateSaveTimer = null;
            if (this.agGrid?.api)
                this.tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
            if (await this.moduleManager.saveState()) {
                this.moduleManager.requestPublishNeeded();
            }
        }, 500/*ms*/);
    }

    /**
     * Retrieves the table state and saves it
     */
    updateTableState() {
        if (!this.agGrid?.api || this.restoreInProgress)
            return;

        // generate state from grid
        const tableState = this.agGridHelperService.getGridState(this.agGrid?.api);
        if (tableState != null) {
            this.tableState = tableState;
        }

        // tell module manager to save the state
        this.queueStateSave();
    };

    restoreColumnState() {
        if (!this.tableState|| !this.agGrid?.api) {
            return;
        }

        // keep state from being saved while restoring
        this.restoreInProgress = true;

        this.agGridHelperService.loadGridState(this.agGrid?.api, this.tableState);

        // states can be saved again
        this.restoreInProgress = false;
    };

    getStateColor(stateName: any) {
        switch (stateName) {
            case EStateName.WithAgent: { 
                return this.STATE_COLORS['green'];
            }
            case EStateName.OutQueue: { 
                return this.STATE_COLORS['teal'];
            }
            case EStateName.InQueue: { 
                return this.STATE_COLORS['orange'];
            }
            case EStateName.PreQueue: { 
                return this.STATE_COLORS['yellow'];
            }
            case EStateName.PostQueue: { 
                return this.STATE_COLORS['blue'];
            }
            default: {
                return this.STATE_COLORS['blue'];
            }
        }
    }

    getLegendLocalizations() {
        const section = 'Contact_States_By_Skill';
        this.contactStates.forEach(state => {
            const key = state.baseName.replace(' ', '_');
            state.localizedName = this.cvLocalizationService.getCachedText(section, key, state.baseName);
        });
    }

    getLocalizedStateName(state: string) {
        return this.contactStates.find(s => s.baseName === state)?.localizedName;
    }

    applyFilter() {
        if (this.agGrid?.api) {
            this.agGrid?.api?.setGridOption('quickFilterText', this.filterValue.trim());
            if (this.showFilter && this.agGrid?.api?.getDisplayedRowCount() === 0 && this.filterValue.length !== 0) {
                this.agGrid?.api?.showNoRowsOverlay();
            } else {
                this.agGrid?.api?.hideOverlay();
            }
        }
    }

    clearFilter() {
        this.filterValue = "";
        this.applyFilter();
    }

    /**
     * Change input focus to the search bar.
     */
    focusOnSearch() {
        // The element won't be ready immediately, so the focus has to be done asynchronously
        setTimeout(() => {
            if (this.config.isCxOne) {
                const el = document.querySelector(`div.cxone-text-input input[name='cxoneInput-${this.moduleKey}']`);
                $(el).focus();
            }
        }, 200);
    }

    private toggleOpenSearch() {
        if (this.showFilter) {
            this.showFilter = false;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', true);
            this.clearFilter();
        } else {
            this.showFilter = true;
            this.agGrid?.api?.setGridOption('suppressNoRowsOverlay', false);
            this.focusOnSearch();
        }
    }
}
